// Stadium Trial — A/B bucket assignment (variant B = "spells").
//
// Assignment rules (see docs/migration_pve_ab_spells.sql):
//   - players.pve_variant set in DB → always wins (lets us force testers).
//   - NULL + hash rollout ON  → stable 50/50 bucket from a hash of profile_id,
//     persisted on first PVE entry so SQL analysis joins stay trivial.
//   - NULL + hash rollout OFF → 'control', NOT persisted (flipping the flag
//     later can still bucket those players).

export type PveVariant = "control" | "spells";

/**
 * Phase 1 rollout switch. false → nobody is hash-bucketed: everyone plays
 * 'control' unless their players.pve_variant was forced to 'spells' in SQL.
 * Flip to true AFTER the tester flow is validated to open the real 50/50.
 */
export const PVE_AB_HASH_ROLLOUT = false;

/** Stable 50/50 bucket from the profile UUID (FNV-1a — deterministic, no DB). */
export function variantFromProfileId(profileId: string): PveVariant {
  let h = 0x811c9dc5;
  for (let i = 0; i < profileId.length; i++) {
    h ^= profileId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 2 === 0 ? "control" : "spells";
}

/**
 * Effective variant for a player: the persisted DB value if set, otherwise the
 * hash bucket (or 'control' while the rollout flag is off). Pure & synchronous —
 * callable before any fetch resolves.
 */
export function resolvePveVariant(
  dbVariant: string | null | undefined,
  profileId: string | undefined
): PveVariant {
  if (dbVariant === "spells" || dbVariant === "control") return dbVariant;
  if (!PVE_AB_HASH_ROLLOUT || !profileId) return "control";
  return variantFromProfileId(profileId);
}
